import { createClient } from '@supabase/supabase-js'

const runtimeEnv = typeof window !== 'undefined' ? window.__DRVN_ENV__ || {} : {}
const readEnv = (key) => process.env[key] || runtimeEnv[key] || ''

const supabaseUrl = readEnv('VUE_APP_SUPABASE_URL')
const supabaseAnonKey = readEnv('VUE_APP_SUPABASE_ANON_KEY')

if (!supabaseUrl || !supabaseAnonKey) {
  // Keep this failure obvious during development.
  throw new Error('Missing Supabase environment variables. Set VUE_APP_SUPABASE_URL and VUE_APP_SUPABASE_ANON_KEY.')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

let activeSession
let initialSessionResolved = false
let resolveInitialSessionPromise = () => {}

const initialSessionPromise = new Promise((resolve) => {
  resolveInitialSessionPromise = resolve
})

const syncActiveSession = (session) => {
  activeSession = session || null

  if (!initialSessionResolved) {
    initialSessionResolved = true
    resolveInitialSessionPromise(activeSession)
  }
}

supabase.auth
  .getSession()
  .then(({ data }) => {
    syncActiveSession(data?.session || null)
  })
  .catch(() => {
    syncActiveSession(null)
  })

supabase.auth.onAuthStateChange((_event, session) => {
  syncActiveSession(session)
})

export const waitForInitialSession = async () => initialSessionPromise

export const refreshReadySession = async () => {
  await waitForInitialSession()

  try {
    const { data, error } = await supabase.auth.getSession()
    if (!error) {
      syncActiveSession(data?.session || null)
    }
  } catch (_error) {
    // keep the last known session if refresh fails
  }

  return activeSession || null
}

export const getReadySession = async () => {
  await waitForInitialSession()
  return activeSession || null
}

export const waitForSession = async (options = {}) => {
  const timeoutMs = Number.isFinite(options.timeoutMs) ? options.timeoutMs : 8000
  const intervalMs = Number.isFinite(options.intervalMs) ? options.intervalMs : 100

  await waitForInitialSession()

  const startedAt = Date.now()

  while (Date.now() - startedAt < timeoutMs) {
    try {
      const { data, error } = await supabase.auth.getSession()
      if (!error && data?.session) {
        syncActiveSession(data.session)
        return data.session
      }
    } catch (_error) {
      // Keep polling until timeout.
    }

    if (activeSession) {
      return activeSession
    }

    await wait(intervalMs)
  }

  return activeSession || null
}

const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms))

export const isRetryableSupabaseError = (error) => {
  if (!error) return false

  const status = Number(error.status || error.statusCode || 0)
  if (status >= 500 || status === 0 || status === 408 || status === 429) {
    return true
  }

  const code = String(error.code || '').toUpperCase()
  if (['ECONNRESET', 'ETIMEDOUT', 'ENOTFOUND'].includes(code)) {
    return true
  }

  const message = String(error.message || error.name || '').toLowerCase()
  return [
    'failed to fetch',
    'fetch failed',
    'network',
    'networkerror',
    'network request failed',
    'load failed',
    'timed out',
    'timeout',
    'aborterror',
    'connection',
  ].some((fragment) => message.includes(fragment))
}

export const getReadableSupabaseErrorMessage = (error, fallbackMessage = 'Something went wrong.') => {
  if (!error) return fallbackMessage

  const rawMessage = String(error.message || error.name || '').toLowerCase()
  if (
    rawMessage.includes('failed to fetch') ||
    rawMessage.includes('fetch failed') ||
    rawMessage.includes('network') ||
    rawMessage.includes('internet_disconnected') ||
    rawMessage.includes('load failed') ||
    rawMessage.includes('connection')
  ) {
    return 'Nu ai conexiune la internet. Incearca din nou.'
  }

  return error.message || fallbackMessage
}

export const runSupabaseQueryWithRetry = async (factory, options = {}) => {
  const retries = Number.isInteger(options.retries) ? options.retries : 2
  const baseDelayMs = Number.isFinite(options.baseDelayMs) ? options.baseDelayMs : 350

  let lastError = null

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const result = await factory(attempt)
      const error = result?.error || null

      if (!error || !isRetryableSupabaseError(error) || attempt === retries) {
        return result
      }

      lastError = error
    } catch (error) {
      if (!isRetryableSupabaseError(error) || attempt === retries) {
        throw error
      }

      lastError = error
    }

    const delayMs = baseDelayMs * (attempt + 1)
    await wait(delayMs)
  }

  return { data: null, error: lastError }
}

const withVersionQuery = (url, version) => {
  if (!url) return ''
  if (!version) return url
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}v=${encodeURIComponent(version)}`
}

export const isExternalAvatarUrl = (url) => {
  if (!url) return false
  const lower = String(url).toLowerCase()
  if (lower.includes('/object/sign/avatars/')) return false
  if (lower.includes('/storage/v1/object/')) return false
  if (supabaseUrl && lower.startsWith(String(supabaseUrl).toLowerCase())) return false
  return true
}

export const getStoragePublicUrl = ({ bucket, path, fallbackUrl = '', version = '' }) => {
  if (path) {
    const { data } = supabase.storage.from(bucket).getPublicUrl(path)
    return withVersionQuery(data?.publicUrl || '', version)
  }

  if (fallbackUrl && String(fallbackUrl).includes(`/object/sign/${bucket}/`)) {
    const pathMatch = String(fallbackUrl).match(new RegExp(`/object/sign/${bucket}/(.+?)\\?`))
    if (pathMatch?.[1]) {
      const { data } = supabase.storage.from(bucket).getPublicUrl(pathMatch[1])
      return withVersionQuery(data?.publicUrl || '', version)
    }
  }

  return withVersionQuery(fallbackUrl || '', version)
}

export const getPostMediaUrl = ({ mediaPath, mediaUrl, version = '' }) => (
  getStoragePublicUrl({
    bucket: 'posts',
    path: mediaPath,
    fallbackUrl: mediaUrl,
    version,
  })
)

export const getPostImageUrl = ({ imagePath, imageUrl, version = '' }) => (
  getPostMediaUrl({
    mediaPath: imagePath,
    mediaUrl: imageUrl,
    version,
  })
)

export const getStoryMediaUrl = ({ mediaPath, mediaUrl, version = '' }) => (
  getStoragePublicUrl({
    bucket: 'stories',
    path: mediaPath,
    fallbackUrl: mediaUrl,
    version,
  })
)

export const getAvatarUrl = ({ avatarPath, avatarUrl, version = '' }) => {
  if (avatarUrl && isExternalAvatarUrl(avatarUrl)) {
    return withVersionQuery(avatarUrl, version)
  }

  return getStoragePublicUrl({
    bucket: 'avatars',
    path: avatarPath,
    fallbackUrl: avatarUrl,
    version,
  })
}
